import { getShareableIdentity } from '@tasted/utils/analytics';
import { withAttribution } from '@tasted/utils/attribution';

// Resolve the web app (tasted.co.za) base URL for cross-app links such as the
// prize redeem page. The web app is a separate origin, so its URL changes per
// environment and can't be inferred from the game's own origin alone.
//
// Priority:
//   1. VITE_WEB_APP_URL — explicit override (set this to https://tasted.co.za in
//      production, or to a fixed host when you want to pin it).
//   2. Dev — derive from the current host, swapping to the web dev port. This is
//      what makes phone testing work: if the game is open at 192.168.10.171:5174,
//      the redeem link becomes 192.168.10.171:5172 (reachable from the phone),
//      and on localhost it becomes localhost:5172.
//   3. Production fallback — tasted.co.za.
const WEB_DEV_PORT = 5172; // apps/web vite server port (strictPort)

export function getWebAppUrl() {
  const override = import.meta.env.VITE_WEB_APP_URL;
  if (override) return override.replace(/\/+$/, '');

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.hostname}:${WEB_DEV_PORT}`;
  }

  // Use the `www` host: it's the canonical Vercel-served origin (apex
  // tasted.co.za redirects to it, and deep links like /redeem only resolve
  // reliably on www — the apex has historically 404'd non-root paths). Emailed
  // prize-redeem links must always land, so default to the host that works.
  return 'https://www.tasted.co.za';
}

/**
 * Where the in-game "Buy a Tasting Box" button sends people.
 *
 * Two things this fixes over the previous bare `https://www.tasted.co.za`:
 *
 * 1. It lands on the boxes, not the top of the landing page. The buy button is
 *    pressed at the one moment attention is highest — the end of a tasting,
 *    on a phone, mid-party — and it was dropping people several screens above
 *    the thing they had just asked to buy.
 * 2. It carries where the click came from. Purchases happen on a different
 *    origin from the game, so without this the funnel ends at the click:
 *    19 buy clicks from real games against 8 orders, with nothing joining the
 *    two ends together. `src`/`session` land as query params the web app reads
 *    on load and attaches to its own events.
 *
 * TAS-371 adds `t=` on top: one opaque token carrying session, player, source
 * and campaign together, which is the value the website stores for the visit
 * and hands to create_order. The four legacy params stay alongside it for the
 * transition — a website build older than this one still reads them.
 */
export function getBuyBoxUrl({ sessionCode, userId, source = 'game_final', campaign = 'buy_box', box } = {}) {
  // Anchor must follow the query string, or the browser treats the whole
  // "?src=..." as part of the fragment and the params never arrive.
  // withAttribution() splits the fragment off and re-attaches it for this reason.
  // `box` (a BOXES id) makes the website open its cart with that box already
  // added (Home's ?box= deep link).
  const query = box ? `?box=${encodeURIComponent(box)}` : '';
  return withAttribution(`${getWebAppUrl()}/${query}#boxes`, {
    sessionId: sessionCode,
    sessionUserId: userId,
    source,
    campaign,
    // `mp_id` carries this browser's Mixpanel identity across to the other
    // origin, which is what makes the buyer ONE person instead of two (TAS-341).
    // src/session/player already told the website where the visit came from;
    // they could not tell it that the visitor was the same human.
    //
    // Only set for an ALREADY-IDENTIFIED player. Mixpanel refuses a `$device:`
    // id as an identify() target, so passing an anonymous one produced a merge
    // that quietly did nothing — shareableIdentity() returns null in that
    // case and the link simply goes without it, as it did before.
    mpId: shareableIdentity(),
    legacySrc: 'game',
  });
}

/**
 * The gamecode-gated wine shop, for "buy this wine" on the end-of-game page.
 *
 * `code` and `wine` are functional — the shop lands on that bottle and checks
 * the game is inside its 30-day window — so they stay ordinary params. The
 * token rides alongside and says who tapped.
 */
export function getWineShopUrl({ sessionCode, wineId, userId, source = 'game_final', campaign = 'buy_wine' } = {}) {
  const params = new URLSearchParams();
  if (sessionCode) params.set('code', String(sessionCode));
  if (wineId) params.set('wine', String(wineId));
  const qs = params.toString();
  return withAttribution(`${getWebAppUrl()}/wine-shop${qs ? `?${qs}` : ''}`, {
    sessionId: sessionCode,
    sessionUserId: userId,
    source,
    campaign,
    mpId: shareableIdentity(),
  });
}

/**
 * The prize winner's shop link: arrive with the discount already applied.
 *
 * Deliberately the recap's `claimUrl` shape (?discount=&email=#boxes), NOT
 * /redeem?code= — CommerceOverlay reads those two straight off the URL.
 */
export function getPrizeShopUrl({ code, email, sessionCode, userId, campaign = 'prize_claim' } = {}) {
  const params = new URLSearchParams();
  if (code) params.set('discount', String(code));
  if (email) params.set('email', String(email));
  const qs = params.toString();
  return withAttribution(`${getWebAppUrl()}/${qs ? `?${qs}` : ''}#boxes`, {
    sessionId: sessionCode,
    sessionUserId: userId,
    source: 'game_reveal',
    campaign,
    mpId: shareableIdentity(),
    legacySrc: 'game-prize',
  });
}

/** Never let an analytics read break a buy button. */
function shareableIdentity() {
  try {
    return getShareableIdentity();
  } catch {
    return null;
  }
}
